import React, { useEffect, useState, useRef, useMemo } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Chip,
  OutlinedInput,
  ListItemText,
  TableCell,
  TableRow,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Paper,
  Link,
  Divider,
  Stack,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import ImageIcon from '@mui/icons-material/Image';
import ClearIcon from '@mui/icons-material/Clear';
import DownloadIcon from '@mui/icons-material/Download';
import api from '../config/api';
import DataTable from '../components/DataTable';
import * as XLSX from 'xlsx';

function Trainers() {
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [cities, setCities] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    gender: '',
    device_token: '',
    // category: 'home',
    category_id: '',
    service_type: 'home_services',
    subcategory_ids: [],
    district: '',
    state_id: '',
    district_id: '',
    city_id: '',
    reference_code: '',
    referral_code: '',
    referral_reward_coins: 100.00,
    referral_reward_percentage: 20.00,
    status: 'active',
    email_verified: true,
    document_url: '',
    certificates_url: [],
  });
  const [documentFile, setDocumentFile] = useState(null);
  const [certificatesFiles, setCertificatesFiles] = useState([]);
  const [documentPreview, setDocumentPreview] = useState(null);
  const [certificatesPreviews, setCertificatesPreviews] = useState([]);
  const [error, setError] = useState('');
  const documentInputRef = useRef(null);
  const certificatesInputRef = useRef(null);
  
  // Filter states
  const [filterServiceType, setFilterServiceType] = useState('');
  const [filterState, setFilterState] = useState('');
  const [filterDistrict, setFilterDistrict] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterSubcategory, setFilterSubcategory] = useState('');
  const [filterGender, setFilterGender] = useState('');
  const [filterEmail, setFilterEmail] = useState('');
  const [filterPhone, setFilterPhone] = useState('');
  const [filterDistricts, setFilterDistricts] = useState([]);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewTrainer, setViewTrainer] = useState(null);
  const [viewImagePreviewOpen, setViewImagePreviewOpen] = useState(false);
  const [viewImagePreviewSrc, setViewImagePreviewSrc] = useState('');
  const [formAttachmentPreviewOpen, setFormAttachmentPreviewOpen] = useState(false);
  const [formAttachmentPreview, setFormAttachmentPreview] = useState({
    title: '',
    src: '',
    isImage: false,
    downloadUrl: '',
    downloadName: '',
  });
  const formAttachmentPreviewRevokeRef = useRef(null);

  const normalizeCertUrls = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value.filter(Boolean);
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) return parsed.filter(Boolean);
      } catch (e) {
        // ignore parse errors, fallback below
      }
      return value
        .split(',')
        .map(item => item.trim())
        .filter(Boolean);
    }
    return [];
  };

  const isImageFile = (fileName) => {
    if (!fileName) return false;
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
    return imageExtensions.some(ext => fileName.toLowerCase().endsWith(ext));
  };

  const isPdfFile = (fileName) => {
    if (!fileName) return false;
    return String(fileName).toLowerCase().endsWith('.pdf');
  };

  const getFileIcon = (fileName) => {
    if (!fileName) return <DescriptionIcon />;
    const lowerName = fileName.toLowerCase();
    if (lowerName.endsWith('.pdf')) return <PictureAsPdfIcon />;
    if (isImageFile(fileName)) return <ImageIcon />;
    return <DescriptionIcon />;
  };

  const createPreviewUrl = (file) => {
    if (!file) return null;
    if (isImageFile(file.name)) {
      return URL.createObjectURL(file);
    }
    return null;
  };

  const handleDocumentFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setDocumentFile(file);
      const preview = createPreviewUrl(file);
      setDocumentPreview(preview);
    } else {
      setDocumentFile(null);
      setDocumentPreview(null);
    }
  };

  const handleCertificatesFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    setCertificatesFiles(files);
    const previews = files.map(file => ({
      file,
      preview: createPreviewUrl(file),
      name: file.name,
    }));
    setCertificatesPreviews(previews);
  };

  const removeCertificatePreview = (index) => {
    const newFiles = certificatesFiles.filter((_, i) => i !== index);
    const newPreviews = certificatesPreviews.filter((_, i) => i !== index);
    
    // Revoke object URL if it exists
    if (certificatesPreviews[index]?.preview) {
      URL.revokeObjectURL(certificatesPreviews[index].preview);
    }
    
    setCertificatesFiles(newFiles);
    setCertificatesPreviews(newPreviews);
    
    // Reset file input
    if (certificatesInputRef.current) {
      certificatesInputRef.current.value = '';
    }
  };

  const resolveTrainerAssetUrl = (url) => {
    if (!url) return '';
    const s = String(url).trim();
    if (s.startsWith('http://') || s.startsWith('https://')) return s;
    const apiBase = process.env.REACT_APP_API_URL || '';
    const originBase = apiBase.replace(/\/api\/?$/, '');
    const path = s.startsWith('/') ? s : `/${s}`;
    return `${originBase || apiBase}${path}`;
  };

  const getFileUrl = (url) => (url ? resolveTrainerAssetUrl(url) : null);

  const openViewImagePreview = (src) => {
    if (!src) return;
    setViewImagePreviewSrc(src);
    setViewImagePreviewOpen(true);
  };

  const closeFormAttachmentPreview = () => {
    if (formAttachmentPreviewRevokeRef.current) {
      URL.revokeObjectURL(formAttachmentPreviewRevokeRef.current);
      formAttachmentPreviewRevokeRef.current = null;
    }
    setFormAttachmentPreviewOpen(false);
    setFormAttachmentPreview({
      title: '',
      src: '',
      isImage: false,
      downloadUrl: '',
      downloadName: '',
    });
  };

  const openFormAttachmentPreview = ({ title, src, isImage, downloadUrl, downloadName, revokeOnClose }) => {
    if (formAttachmentPreviewRevokeRef.current) {
      URL.revokeObjectURL(formAttachmentPreviewRevokeRef.current);
      formAttachmentPreviewRevokeRef.current = null;
    }
    if (revokeOnClose) formAttachmentPreviewRevokeRef.current = revokeOnClose;
    setFormAttachmentPreview({
      title,
      src,
      isImage,
      downloadUrl: downloadUrl || src,
      downloadName: downloadName || title,
    });
    setFormAttachmentPreviewOpen(true);
  };

  const downloadTrainerBlobFile = (file) => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name || 'download';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  const downloadTrainerHref = (href, filename) => {
    if (!href) return;
    const a = document.createElement('a');
    a.href = href;
    a.download = filename || 'download';
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const openEditDocumentInModal = () => {
    if (documentFile) {
      const name = documentFile.name;
      if (isImageFile(name) && documentPreview) {
        openFormAttachmentPreview({
          title: name,
          src: documentPreview,
          isImage: true,
          downloadUrl: documentPreview,
          downloadName: name,
          revokeOnClose: null,
        });
        return;
      }
      const blobUrl = URL.createObjectURL(documentFile);
      openFormAttachmentPreview({
        title: name,
        src: blobUrl,
        isImage: false,
        downloadUrl: blobUrl,
        downloadName: name,
        revokeOnClose: blobUrl,
      });
      return;
    }
    if (formData.document_url) {
      const full = resolveTrainerAssetUrl(formData.document_url);
      const name = formData.document_url.split('/').pop();
      openFormAttachmentPreview({
        title: name,
        src: full,
        isImage: isImageFile(formData.document_url),
        downloadUrl: full,
        downloadName: name,
        revokeOnClose: null,
      });
    }
  };

  const handleDownloadEditDocument = (e) => {
    e?.stopPropagation?.();
    if (documentFile) {
      downloadTrainerBlobFile(documentFile);
      return;
    }
    if (formData.document_url) {
      const full = resolveTrainerAssetUrl(formData.document_url);
      downloadTrainerHref(full, formData.document_url.split('/').pop());
    }
  };

  const openEditCertNewInModal = (preview) => {
    const { file, preview: blobPreview, name } = preview;
    if (blobPreview && isImageFile(name)) {
      openFormAttachmentPreview({
        title: name,
        src: blobPreview,
        isImage: true,
        downloadUrl: blobPreview,
        downloadName: name,
        revokeOnClose: null,
      });
      return;
    }
    const blobUrl = URL.createObjectURL(file);
    openFormAttachmentPreview({
      title: name,
      src: blobUrl,
      isImage: false,
      downloadUrl: blobUrl,
      downloadName: name,
      revokeOnClose: blobUrl,
    });
  };

  const handleDownloadCertNew = (preview, e) => {
    e?.stopPropagation?.();
    downloadTrainerBlobFile(preview.file);
  };

  const openEditCertExistingInModal = (url) => {
    const full = resolveTrainerAssetUrl(url);
    const name = url.split('/').pop();
    openFormAttachmentPreview({
      title: name,
      src: full,
      isImage: isImageFile(url),
      downloadUrl: full,
      downloadName: name,
      revokeOnClose: null,
    });
  };

  const handleDownloadCertExisting = (url, e) => {
    e?.stopPropagation?.();
    const full = resolveTrainerAssetUrl(url);
    downloadTrainerHref(full, url.split('/').pop());
  };

  useEffect(() => {
    fetchTrainers();
    fetchCategories();
    fetchStates();
  }, []);

  useEffect(() => {
    if (filterState) {
      fetchDistrictsForFilter(filterState);
    } else {
      setFilterDistricts([]);
      setFilterDistrict('');
    }
  }, [filterState]);

  // Cleanup preview URLs on unmount
  useEffect(() => {
    return () => {
      if (formAttachmentPreviewRevokeRef.current) {
        URL.revokeObjectURL(formAttachmentPreviewRevokeRef.current);
        formAttachmentPreviewRevokeRef.current = null;
      }
      if (documentPreview) {
        URL.revokeObjectURL(documentPreview);
      }
      certificatesPreviews.forEach(preview => {
        if (preview.preview) {
          URL.revokeObjectURL(preview.preview);
        }
      });
    };
  }, [documentPreview, certificatesPreviews]);

  const fetchTrainers = async () => {
    try {
      const response = await api.get('/trainers');
      setTrainers(response.data.data || []);
    } catch (error) {
      console.error('Error fetching trainers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories');
      setCategories(response.data.data || []);
      // Flatten all subcategories
      const allSubcategories = [];
      response.data.data.forEach(cat => {
        if (cat.subcategories) {
          cat.subcategories.forEach(sub => {
            allSubcategories.push({ ...sub, category_name: cat.name });
          });
        }
      });
      setSubcategories(allSubcategories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchStates = async () => {
    try {
      const response = await api.get('/locations/states');
      setStates(response.data.data || []);
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  };

  const fetchDistricts = async (stateId) => {
    if (!stateId) {
      setDistricts([]);
      setCities([]);
      return;
    }
    try {
      const response = await api.get(`/locations/states/${stateId}/districts`);
      setDistricts(response.data.data || []);
      setCities([]); // Reset cities when district changes
    } catch (error) {
      console.error('Error fetching districts:', error);
    }
  };

  const fetchCities = async (districtId) => {
    if (!districtId) {
      setCities([]);
      return;
    }
    try {
      const response = await api.get(`/locations/districts/${districtId}/cities`);
      setCities(response.data.data || []);
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };

  const fetchDistrictsForFilter = async (stateId) => {
    if (!stateId) {
      setFilterDistricts([]);
      return;
    }
    try {
      const response = await api.get(`/locations/states/${stateId}/districts`);
      setFilterDistricts(response.data.data || []);
    } catch (error) {
      console.error('Error fetching districts for filter:', error);
    }
  };

  const handleOpen = async (trainer = null) => {
    if (trainer) {
      setEditing(trainer);
      setFormData({
        name: trainer.name || '',
        email: trainer.email || '',
        password: '',
        phone: trainer.phone || '',
        gender: trainer.gender || '',
        device_token: trainer.device_token || '',
        category: trainer.category || 'home',
        category_id: trainer.category_id || '',
        service_type: trainer.service_type || 'home_services',
        subcategory_ids: trainer.subcategories ? trainer.subcategories.map(s => s.id) : [],
        district: trainer.district || '',
        state_id: trainer.state_id || '',
        district_id: trainer.district_id || '',
        city_id: trainer.city_id || '',
        reference_code: trainer.reference_code || '',
        referral_code: '',
        referral_reward_coins: trainer.referral_reward_coins || 100.00,
        referral_reward_percentage: trainer.referral_reward_percentage || 20.00,
        status: trainer.status || 'active',
        email_verified: trainer.email_verified !== undefined ? trainer.email_verified : true,
        document_url: trainer.document_url || '',
        certificates_url: normalizeCertUrls(trainer.certificates_url),
      });
      // Fetch districts and cities if IDs are available
      if (trainer.state_id) {
        await fetchDistricts(trainer.state_id);
      }
      if (trainer.district_id) {
        await fetchCities(trainer.district_id);
      }
      setDocumentFile(null);
      setCertificatesFiles([]);
    } else {
      setEditing(null);
      setFormData({
        name: '',
        email: '',
        password: '',
        phone: '',
        gender: '',
        device_token: '',
        category: 'home',
        category_id: '',
        service_type: 'home_services',
        subcategory_ids: [],
        district: '',
        state_id: '',
        district_id: '',
        city_id: '',
        reference_code: '',
        referral_code: '',
        referral_reward_coins: 100.00,
        referral_reward_percentage: 20.00,
        status: 'active',
        email_verified: true,
        document_url: '',
        certificates_url: [],
      });
      setDistricts([]);
      setCities([]);
      setDocumentFile(null);
      setCertificatesFiles([]);
      setDocumentPreview(null);
      setCertificatesPreviews([]);
    }
    setOpen(true);
    setError('');
  };

  const handleClose = () => {
    closeFormAttachmentPreview();
    setOpen(false);
    setEditing(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      phone: '',
      gender: '',
      device_token: '',
      category: 'home',
      category_id: '',
      subcategory_ids: [],
      district: '',
      state_id: '',
      district_id: '',
      city_id: '',
      reference_code: '',
      referral_code: '',
      referral_reward_coins: 100.00,
      referral_reward_percentage: 20.00,
      status: 'active',
      email_verified: true,
      document_url: '',
      certificates_url: [],
    });
    setDistricts([]);
    setCities([]);
    setDocumentFile(null);
    setCertificatesFiles([]);
    // Clean up preview URLs
    if (documentPreview) {
      URL.revokeObjectURL(documentPreview);
    }
    certificatesPreviews.forEach(preview => {
      if (preview.preview) {
        URL.revokeObjectURL(preview.preview);
      }
    });
    setDocumentPreview(null);
    setCertificatesPreviews([]);
    setError('');
  };

  const handleSubmit = async () => {
    try {
      setError('');
      const formDataToSend = new FormData();

      // Add all form fields
      Object.keys(formData).forEach(key => {
        if (key === 'subcategory_ids') {
          formDataToSend.append(key, JSON.stringify(formData[key]));
        } else if (key === 'certificates_url') {
          const normalized = normalizeCertUrls(formData[key]);
          formDataToSend.append(key, JSON.stringify(normalized));
        } else if (key === 'service_type') {
          // Always include service_type, even if it's the default value
          formDataToSend.append(key, formData[key] || 'home_services');
        } else if (formData[key] !== null && formData[key] !== undefined && formData[key] !== '') {
          formDataToSend.append(key, formData[key]);
        }
      });

      // Add files if selected
      if (documentFile) {
        formDataToSend.append('document', documentFile);
      }
      if (certificatesFiles && certificatesFiles.length > 0) {
        certificatesFiles.forEach(file => {
          formDataToSend.append('certificates', file);
        });
      }

      if (editing) {
        await api.put(`/trainers/${editing.id}`, formDataToSend, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      } else {
        await api.post('/trainers', formDataToSend, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      }
      handleClose();
      fetchTrainers();
    } catch (error) {
      setError(error.response?.data?.error || 'Error saving trainer');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this trainer?')) {
      try {
        await api.delete(`/trainers/${id}`);
        fetchTrainers();
      } catch (error) {
        console.error('Error deleting trainer:', error);
      }
    }
  };

  const handleViewOpen = (trainer) => {
    setViewTrainer(trainer);
    setViewOpen(true);
  };

  const handleViewClose = () => {
    setViewOpen(false);
    setViewTrainer(null);
    setViewImagePreviewOpen(false);
    setViewImagePreviewSrc('');
  };

  const getSubcategoriesForCategory = (categoryId) => {
    if (!categoryId) return [];
    return subcategories.filter(sub => sub.category_id === parseInt(categoryId));
  };

  // Clear all filters
  const handleClearFilters = () => {
    setFilterServiceType('');
    setFilterState('');
    setFilterDistrict('');
    setFilterCategory('');
    setFilterSubcategory('');
    setFilterGender('');
    setFilterEmail('');
    setFilterPhone('');
    setFilterDistricts([]);
  };

  // Filter trainers based on service type, state, district, category, and subcategory
  const filteredTrainers = useMemo(() => {
    let filtered = [...trainers];

    if (filterServiceType) {
      filtered = filtered.filter(trainer => trainer.service_type === filterServiceType);
    }

    if (filterState) {
      filtered = filtered.filter(trainer => trainer.state_id === parseInt(filterState));
    }

    if (filterDistrict) {
      filtered = filtered.filter(trainer => trainer.district_id === parseInt(filterDistrict));
    }

    if (filterCategory) {
      filtered = filtered.filter(trainer => trainer.category_id === parseInt(filterCategory));
    }

    if (filterSubcategory) {
      filtered = filtered.filter(trainer => {
        if (!trainer.subcategories || !Array.isArray(trainer.subcategories)) return false;
        return trainer.subcategories.some(sub => sub.id === parseInt(filterSubcategory));
      });
    }

    if (filterGender) {
      filtered = filtered.filter(trainer => (trainer.gender || '').toLowerCase() === filterGender.toLowerCase());
    }

    if (filterEmail) {
      const emailSearch = filterEmail.toLowerCase();
      filtered = filtered.filter(trainer => (trainer.email || '').toLowerCase().includes(emailSearch));
    }

    if (filterPhone) {
      const phoneSearch = filterPhone.toLowerCase();
      filtered = filtered.filter(trainer => (trainer.phone || '').toLowerCase().includes(phoneSearch));
    }

    return filtered;
  }, [trainers, filterServiceType, filterState, filterDistrict, filterCategory, filterSubcategory, filterGender, filterEmail, filterPhone]);

  const genderCounts = useMemo(() => {
    return trainers.reduce((acc, trainer) => {
      const key = (trainer.gender || '').toLowerCase();
      if (key) {
        acc[key] = (acc[key] || 0) + 1;
      }
      return acc;
    }, {});
  }, [trainers]);

  // Export trainers to Excel
  const handleExportExcel = async () => {
    const stateIds = [...new Set(filteredTrainers.map((t) => t.state_id).filter(Boolean))];
    const districtsMap = new Map();

    for (const stateId of stateIds) {
      try {
        const response = await api.get(`/locations/states/${stateId}/districts`);
        const districtList = response.data.data || [];
        districtList.forEach((district) => {
          districtsMap.set(district.id, district.name);
        });
      } catch (err) {
        console.error(`Error fetching districts for state ${stateId}:`, err);
      }
    }

    const exportData = filteredTrainers.map((trainer) => ({
      'ID': trainer.id,
      'Name': trainer.name || '',
      'Email': trainer.email || '',
      'Phone': trainer.phone || '',
      'Professional Specializations': trainer.category_info?.name || trainer.category || '',
      'Subcategories': trainer.subcategories?.map((s) => s.name).join(', ') || '',
      'Service Type': trainer.service_type || '',
      'State': states.find((s) => s.id === trainer.state_id)?.name || '',
      'District': districtsMap.get(trainer.district_id) || '',
      'Reference Code': trainer.reference_code || '',
      'Coins': trainer.coins ?? '',
      'Status': trainer.status || '',
    }));

    if (exportData.length === 0) {
      alert('No data to export');
      return;
    }

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Trainers');

    const maxWidth = 50;
    const wscols = Object.keys(exportData[0] || {}).map(() => ({ wch: maxWidth }));
    ws['!cols'] = wscols;

    XLSX.writeFile(wb, `trainers_export_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Trainers</Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExportExcel}
            disabled={filteredTrainers.length === 0}
          >
            Download Excel
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
            Add Trainer
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 1.5, mb: 2 }}>
        <Typography variant="body1">
          Showing <strong>{filteredTrainers.length}</strong> trainer(s) out of <strong>{trainers.length}</strong>
        </Typography>
      </Paper>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            Filters
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ClearIcon />}
            onClick={handleClearFilters}
            disabled={!filterServiceType && !filterState && !filterDistrict && !filterCategory && !filterSubcategory && !filterGender && !filterEmail && !filterPhone}
          >
            Clear Filters
          </Button>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Service Type</InputLabel>
              <Select
                value={filterServiceType}
                onChange={(e) => setFilterServiceType(e.target.value)}
                label="Service Type"
              >
                <MenuItem value="">All Service Types</MenuItem>
                <MenuItem value="home_services">Home Services</MenuItem>
                <MenuItem value="online_services">Online Services</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>State</InputLabel>
              <Select
                value={filterState}
                onChange={(e) => {
                  setFilterState(e.target.value);
                  setFilterDistrict('');
                }}
                label="State"
              >
                <MenuItem value="">All States</MenuItem>
                {states.map((state) => (
                  <MenuItem key={state.id} value={state.id}>
                    {state.name} ({trainers.filter(t => t.state_id === state.id).length})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>District</InputLabel>
              <Select
                value={filterDistrict}
                onChange={(e) => setFilterDistrict(e.target.value)}
                label="District"
                disabled={!filterState}
              >
                <MenuItem value="">All Districts</MenuItem>
                {filterDistricts.map((district) => (
                  <MenuItem key={district.id} value={district.id}>
                    {district.name} ({trainers.filter(t => t.district_id === district.id).length})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={filterCategory}
                onChange={(e) => {
                  setFilterCategory(e.target.value);
                  setFilterSubcategory('');
                }}
                label="Category"
              >
                <MenuItem value="">All Categories</MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.name} ({trainers.filter(t => t.category_id === cat.id).length})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Subcategory</InputLabel>
              <Select
                value={filterSubcategory}
                onChange={(e) => setFilterSubcategory(e.target.value)}
                label="Subcategory"
                disabled={!filterCategory}
              >
                <MenuItem value="">All Subcategories</MenuItem>
                {getSubcategoriesForCategory(filterCategory).map((subcat) => (
                  <MenuItem key={subcat.id} value={subcat.id}>
                    {subcat.name} ({trainers.filter(t => t.subcategories?.some(s => s.id === subcat.id)).length})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Gender</InputLabel>
              <Select
                value={filterGender}
                onChange={(e) => setFilterGender(e.target.value)}
                label="Gender"
              >
                <MenuItem value="">All Genders ({trainers.length})</MenuItem>
                <MenuItem value="male">Male ({genderCounts.male || 0})</MenuItem>
                <MenuItem value="female">Female ({genderCounts.female || 0})</MenuItem>
                <MenuItem value="other">Other ({genderCounts.other || 0})</MenuItem>
                <MenuItem value="others">Others ({genderCounts.others || 0})</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              label="Search Email"
              value={filterEmail}
              onChange={(e) => setFilterEmail(e.target.value)}
              placeholder="Enter email..."
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              label="Search Mobile"
              value={filterPhone}
              onChange={(e) => setFilterPhone(e.target.value)}
              placeholder="Enter mobile..."
            />
          </Grid>
        </Grid>
      </Paper>

      <DataTable
        data={filteredTrainers}
        loading={loading}
        searchFields={['name', 'email', 'phone', 'reference_code']}
        emptyMessage="No trainers found"
        columns={[
          { field: 'id', label: 'ID' },
          { field: 'name', label: 'Name' },
          { field: 'email', label: 'Email' },
          { field: 'phone', label: 'Phone' },
          { field: 'category', label: 'Professional Specializations' },
          { field: 'purchased_plan', label: 'Purchased Plan' },
          { field: 'reference_code', label: 'Reference Code' },
          { field: 'coins', label: 'Coins' },
          { field: 'status', label: 'Status' },
          { field: 'actions', label: 'Actions' },
        ]}
        renderRow={(trainer) => (
          <TableRow key={trainer.id}>
            <TableCell>{trainer.id}</TableCell>
            <TableCell>{trainer.name}</TableCell>
            <TableCell>{trainer.email}</TableCell>
            <TableCell>{trainer.phone || '-'}</TableCell>
            <TableCell>
              {trainer.category_info ? trainer.category_info.name : (trainer.category || 'home')}
            </TableCell>
            <TableCell>
              {trainer.latest_purchase_plan ? (
                <Box>
                  <Typography variant="body2">
                    {trainer.latest_purchase_plan.plan_amount != null
                      ? `INR ${trainer.latest_purchase_plan.plan_amount}`
                      : `Credited: ${trainer.latest_purchase_plan.credited_amount || 0}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {trainer.latest_purchase_plan.plan_coins != null
                      ? `${trainer.latest_purchase_plan.plan_coins} coins`
                      : ''}
                    {trainer.latest_purchase_plan.device_type
                      ? ` - ${trainer.latest_purchase_plan.device_type}`
                      : ''}
                  </Typography>
                </Box>
              ) : (
                '-'
              )}
            </TableCell>
            <TableCell>{trainer.reference_code || '-'}</TableCell>
            <TableCell>{trainer.coins || 0}</TableCell>
            <TableCell>{trainer.status}</TableCell>
            <TableCell>
              <IconButton size="small" onClick={() => handleViewOpen(trainer)}>
                <VisibilityIcon />
              </IconButton>
              <IconButton size="small" onClick={() => handleOpen(trainer)}>
                <EditIcon />
              </IconButton>
              <IconButton size="small" onClick={() => handleDelete(trainer.id)}>
                <DeleteIcon />
              </IconButton>
            </TableCell>
          </TableRow>
        )}
      />

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Edit Trainer' : 'Add Trainer'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField
            fullWidth
            label="Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Password"
            type="password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            margin="normal"
            helperText={editing ? "Leave empty to keep current password" : "Required for new trainers"}
            required={!editing}
            autoComplete={false}
          />
          <TextField
            fullWidth
            label="Phone"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            margin="normal"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Gender</InputLabel>
            <Select
              value={formData.gender || ''}
              onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
              label="Gender"
            >
              <MenuItem value="">None</MenuItem>
              <MenuItem value="male">Male</MenuItem>
              <MenuItem value="female">Female</MenuItem>
              <MenuItem value="others">Others</MenuItem>
            </Select>
          </FormControl>
          {/* <TextField
            fullWidth
            label="Device Token"
            value={formData.device_token}
            onChange={(e) => setFormData({ ...formData, device_token: e.target.value })}
            margin="normal"
            multiline
            rows={2}
          /> */}
          {/* <FormControl fullWidth margin="normal">
            <InputLabel>Category (Legacy)</InputLabel>
            <Select
              value={formData.category || 'home'}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              label="Category (Legacy)"
            >
              <MenuItem value="home">Home</MenuItem>
              <MenuItem value="arotho">Arotho</MenuItem>
              <MenuItem value="physical">Physical</MenuItem>
            </Select>
          </FormControl> */}
          <FormControl fullWidth margin="normal">
            <InputLabel>Professional Specializations</InputLabel>
            <Select
              value={formData.category_id || ''}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  category_id: e.target.value,
                  subcategory_ids: [] // Reset subcategories when category changes
                });
              }}
              label="Professional Specializations"
            >
              <MenuItem value="">None</MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>
                  {cat.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Providing Services</InputLabel>
            <Select
              multiple
              value={formData.subcategory_ids || []}
              onChange={(e) => setFormData({ ...formData, subcategory_ids: e.target.value })}
              input={<OutlinedInput label="Providing Services" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    const subcat = subcategories.find(s => s.id === value);
                    return subcat ? (
                      <Chip key={value} label={subcat.name} size="small" />
                    ) : null;
                  })}
                </Box>
              )}
            >
              {getSubcategoriesForCategory(formData.category_id).map((subcat) => (
                <MenuItem key={subcat.id} value={subcat.id}>
                  <Checkbox checked={formData.subcategory_ids?.indexOf(subcat.id) > -1} />
                  <ListItemText primary={subcat.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Service Type</InputLabel>
            <Select
              value={formData.service_type || 'home_services'}
              onChange={(e) => {
                const newValue = e.target.value;
                setFormData({ ...formData, service_type: newValue });
              }}
              label="Service Type"
            >
              <MenuItem value="home_services">Home Services</MenuItem>
              <MenuItem value="online_services">Online Services</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>State</InputLabel>
            <Select
              value={formData.state_id || ''}
              onChange={async (e) => {
                const stateId = e.target.value;
                setFormData({
                  ...formData,
                  state_id: stateId,
                  district_id: '',
                  city_id: '',
                  // district: ''
                });
                await fetchDistricts(stateId);
              }}
              label="State"
            >
              <MenuItem value="">None</MenuItem>
              {states.map((state) => (
                <MenuItem key={state.id} value={state.id}>
                  {state.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>District</InputLabel>
            <Select
              value={formData.district_id || ''}
              onChange={async (e) => {
                const districtId = e.target.value;
                setFormData({
                  ...formData,
                  district_id: districtId,
                  city_id: ''
                });
                await fetchCities(districtId);
              }}
              label="District"
              disabled={!formData.state_id}
            >
              <MenuItem value="">None</MenuItem>
              {districts.map((district) => (
                <MenuItem key={district.id} value={district.id}>
                  {district.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* <FormControl fullWidth margin="normal">
            <InputLabel>City</InputLabel>
            <Select
              value={formData.city_id || ''}
              onChange={(e) => {
                setFormData({ ...formData, city_id: e.target.value });
              }}
              label="City"
              disabled={!formData.district_id}
            >
              <MenuItem value="">None</MenuItem>
              {cities.map((city) => (
                <MenuItem key={city.id} value={city.id}>
                  {city.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl> */}
          {/* <TextField
            fullWidth
            label="District (Legacy)"
            value={formData.district}
            onChange={(e) => setFormData({ ...formData, district: e.target.value })}
            margin="normal"
            helperText="Optional: Use if district dropdown is not available"
          /> */}
          {/* <TextField
            fullWidth
            label="Reference Code"
            value={formData.reference_code}
            onChange={(e) => setFormData({ ...formData, reference_code: e.target.value })}
            margin="normal"
            helperText="Unique code for referral rewards (auto-generated if empty)"
            hiddenLabel
          /> */}
          {/* <TextField
            fullWidth
            label="Referral Reward Coins"
            type="number"
            value={formData.referral_reward_coins}
            onChange={(e) => setFormData({ ...formData, referral_reward_coins: e.target.value })}
            margin="normal"
            helperText="Fixed coins reward for direct registrations"
            hidden={true}
          /> */}
          <TextField
            fullWidth
            label="Referral Reward Percentage"
            type="number"
            value={formData.referral_reward_percentage}
            onChange={(e) => setFormData({ ...formData, referral_reward_percentage: e.target.value })}
            margin="normal"
            helperText="Percentage reward for external registrations (e.g., 20 for 20%)"
            onWheel={(e) => e.target.blur()}
          />
          {/* {!editing && (
            <TextField
              fullWidth
              label="Referral Code (Optional)"
              value={formData.referral_code || ''}
              onChange={(e) => setFormData({ ...formData, referral_code: e.target.value })}
              margin="normal"
              helperText="Enter another trainer's reference code to reward them when this trainer registers"
              hiddenLabel
            />
          )} */}
          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>Document Upload</Typography>
            <input
              ref={documentInputRef}
              type="file"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              onChange={handleDocumentFileChange}
              style={{ width: '100%' }}
            />
            
            {/* Document Preview */}
            {(documentPreview || (formData.document_url && !documentFile)) && (
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Card variant="outlined">
                      <Box
                        onClick={openEditDocumentInModal}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            openEditDocumentInModal();
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        sx={{ position: 'relative', cursor: 'pointer' }}
                        aria-label="Open document preview"
                      >
                        {documentPreview ? (
                          <CardMedia
                            component="img"
                            height="200"
                            image={documentPreview}
                            alt="Document preview"
                            sx={{ objectFit: 'contain', bgcolor: '#f5f5f5' }}
                          />
                        ) : isImageFile(formData.document_url) ? (
                          <CardMedia
                            component="img"
                            height="200"
                            image={getFileUrl(formData.document_url)}
                            alt="Document"
                            sx={{ objectFit: 'contain', bgcolor: '#f5f5f5' }}
                          />
                        ) : (
                          <Box
                            sx={{
                              height: 200,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: '#f5f5f5',
                            }}
                          >
                            {getFileIcon(formData.document_url)}
                          </Box>
                        )}
                        {documentFile && (
                          <IconButton
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              bgcolor: 'rgba(255, 255, 255, 0.8)',
                              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.9)' },
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setDocumentFile(null);
                              setDocumentPreview(null);
                              if (documentInputRef.current) {
                                documentInputRef.current.value = '';
                              }
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                      <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                          <Typography variant="caption" noWrap sx={{ flex: 1, minWidth: 0 }}>
                            {documentFile ? documentFile.name : formData.document_url.split('/').pop()}
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={handleDownloadEditDocument}
                          >
                            Download
                          </Button>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>

          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>Certificates Upload (multiple allowed)</Typography>
            <input
              ref={certificatesInputRef}
              type="file"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              multiple
              onChange={handleCertificatesFileChange}
              style={{ width: '100%' }}
            />
            
            {/* Certificates Preview List */}
            {(certificatesPreviews.length > 0 || (Array.isArray(formData.certificates_url) && formData.certificates_url.length > 0 && certificatesFiles.length === 0)) && (
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  {/* New file previews */}
                  {certificatesPreviews.map((preview, index) => (
                    <Grid item xs={12} sm={6} md={4} key={`new-${index}`}>
                      <Card variant="outlined">
                        <Box
                          onClick={() => openEditCertNewInModal(preview)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              openEditCertNewInModal(preview);
                            }
                          }}
                          role="button"
                          tabIndex={0}
                          sx={{ position: 'relative', cursor: 'pointer' }}
                          aria-label="Open certificate preview"
                        >
                          {preview.preview ? (
                            <CardMedia
                              component="img"
                              height="200"
                              image={preview.preview}
                              alt={preview.name}
                              sx={{ objectFit: 'contain', bgcolor: '#f5f5f5' }}
                            />
                          ) : (
                            <Box
                              sx={{
                                height: 200,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: '#f5f5f5',
                              }}
                            >
                              {getFileIcon(preview.name)}
                            </Box>
                          )}
                          <IconButton
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              bgcolor: 'rgba(255, 255, 255, 0.8)',
                              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.9)' },
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              removeCertificatePreview(index);
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                            <Typography variant="caption" noWrap sx={{ flex: 1, minWidth: 0 }}>
                              {preview.name}
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<DownloadIcon />}
                              onClick={(e) => handleDownloadCertNew(preview, e)}
                            >
                              Download
                            </Button>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                  
                  {/* Existing certificate URLs */}
                  {Array.isArray(formData.certificates_url) && formData.certificates_url.length > 0 && certificatesFiles.length === 0 && 
                    formData.certificates_url.map((url, index) => (
                      <Grid item xs={12} sm={6} md={4} key={`existing-${index}`}>
                        <Card variant="outlined">
                          <Box
                            onClick={() => openEditCertExistingInModal(url)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                openEditCertExistingInModal(url);
                              }
                            }}
                            role="button"
                            tabIndex={0}
                            sx={{ position: 'relative', cursor: 'pointer' }}
                            aria-label="Open certificate preview"
                          >
                            {isImageFile(url) ? (
                              <CardMedia
                                component="img"
                                height="200"
                                image={getFileUrl(url)}
                                alt={`Certificate ${index + 1}`}
                                sx={{ objectFit: 'contain', bgcolor: '#f5f5f5' }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <Box
                              sx={{
                                height: 200,
                                display: isImageFile(url) ? 'none' : 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: '#f5f5f5',
                              }}
                            >
                              {getFileIcon(url)}
                            </Box>
                          </Box>
                          <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                              <Typography variant="caption" noWrap sx={{ flex: 1, minWidth: 0 }}>
                                {url.split('/').pop()}
                              </Typography>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                onClick={(e) => handleDownloadCertExisting(url, e)}
                              >
                                Download
                              </Button>
                            </Stack>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))
                  }
                </Grid>
              </Box>
            )}
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.email_verified}
                onChange={(e) => setFormData({ ...formData, email_verified: e.target.checked })}
              />
            }
            label="Email Verified"
            sx={{ mt: 1 }}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Status</InputLabel>
            <Select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              label="Status"
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={formAttachmentPreviewOpen}
        onClose={closeFormAttachmentPreview}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>{formAttachmentPreview.title || 'Preview'}</DialogTitle>
        <DialogContent
          sx={{
            bgcolor: 'grey.100',
            minHeight: 200,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {formAttachmentPreview.isImage ? (
            <Box
              component="img"
              src={formAttachmentPreview.src}
              alt=""
              sx={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
            />
          ) : isPdfFile(formAttachmentPreview.title) ? (
            <Box
              component="iframe"
              src={formAttachmentPreview.src}
              title={formAttachmentPreview.title}
              sx={{ width: '100%', height: '70vh', border: 'none', bgcolor: '#fff' }}
            />
          ) : (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              Preview is not available for this file type. Use Download to save or open the file.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<DownloadIcon />}
            variant="contained"
            onClick={() =>
              downloadTrainerHref(
                formAttachmentPreview.downloadUrl,
                formAttachmentPreview.downloadName
              )
            }
          >
            Download
          </Button>
          <Button onClick={closeFormAttachmentPreview}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={viewOpen} onClose={handleViewClose} maxWidth="md" fullWidth>
        <DialogTitle>Trainer Details</DialogTitle>
        <DialogContent>
          {viewTrainer && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2"><strong>ID:</strong> {viewTrainer.id}</Typography>
              <Typography variant="body2"><strong>Name:</strong> {viewTrainer.name || '-'}</Typography>
              <Typography variant="body2"><strong>Email:</strong> {viewTrainer.email || '-'}</Typography>
              <Typography variant="body2"><strong>Phone:</strong> {viewTrainer.phone || '-'}</Typography>
              <Typography variant="body2"><strong>Gender:</strong> {viewTrainer.gender || '-'}</Typography>
              <Typography variant="body2"><strong>Service Type:</strong> {viewTrainer.service_type || '-'}</Typography>
              <Typography variant="body2"><strong>Category:</strong> {viewTrainer.category_info?.name || viewTrainer.category || '-'}</Typography>
              <Typography variant="body2"><strong>Reference Code:</strong> {viewTrainer.reference_code || '-'}</Typography>
              <Typography variant="body2"><strong>Device Type:</strong> {viewTrainer.device_type || '-'}</Typography>
              <Typography variant="body2">
                <strong>Latest Purchased Plan:</strong>{' '}
                {viewTrainer.latest_purchase_plan
                  ? (viewTrainer.latest_purchase_plan.plan_amount != null
                    ? `INR ${viewTrainer.latest_purchase_plan.plan_amount}`
                    : `Credited: ${viewTrainer.latest_purchase_plan.credited_amount || 0}`)
                  : '-'}
              </Typography>
              <Typography variant="body2"><strong>Coins:</strong> {viewTrainer.coins || 0}</Typography>
              <Typography variant="body2"><strong>Status:</strong> {viewTrainer.status || '-'}</Typography>
              <Typography variant="body2"><strong>Email Verified:</strong> {viewTrainer.email_verified ? 'Yes' : 'No'}</Typography>
              <Typography variant="body2" sx={{ mt: 1 }}><strong>Subcategories:</strong></Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {viewTrainer.subcategories?.length
                  ? viewTrainer.subcategories.map((s) => <Chip key={s.id} label={s.name} size="small" />)
                  : <Typography variant="body2">-</Typography>}
              </Box>

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>Attachments</Typography>

              <Typography variant="body2" sx={{ mt: 1 }}><strong>Document</strong></Typography>
              {viewTrainer.document_url ? (
                <Box sx={{ mt: 0.5 }}>
                  {isImageFile(viewTrainer.document_url) ? (
                    <Box
                      component="button"
                      type="button"
                      onClick={() => openViewImagePreview(resolveTrainerAssetUrl(viewTrainer.document_url))}
                      sx={{
                        p: 0,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        background: 'none',
                        cursor: 'pointer',
                        overflow: 'hidden',
                        display: 'block',
                      }}
                      aria-label="Preview document image"
                    >
                      <Box
                        component="img"
                        src={resolveTrainerAssetUrl(viewTrainer.document_url)}
                        alt="Document"
                        sx={{ display: 'block', maxHeight: 160, maxWidth: '100%', objectFit: 'contain' }}
                      />
                    </Box>
                  ) : (
                    <Link
                      href={resolveTrainerAssetUrl(viewTrainer.document_url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                    >
                      Open document ({viewTrainer.document_url.split('/').pop()})
                    </Link>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">-</Typography>
              )}

              <Typography variant="body2" sx={{ mt: 2 }}><strong>Certificates</strong></Typography>
              {(() => {
                const certs = normalizeCertUrls(viewTrainer.certificates_url || viewTrainer.certificates);
                if (!certs.length) {
                  return <Typography variant="body2" color="text.secondary">-</Typography>;
                }
                return (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1 }}>
                    {certs.map((certUrl, index) => {
                      const fullUrl = resolveTrainerAssetUrl(certUrl);
                      const fileLabel = certUrl.split('/').pop() || `Certificate ${index + 1}`;
                      if (isImageFile(certUrl)) {
                        return (
                          <Box
                            key={`${certUrl}-${index}`}
                            component="button"
                            type="button"
                            onClick={() => openViewImagePreview(fullUrl)}
                            sx={{
                              p: 0,
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              background: 'none',
                              cursor: 'pointer',
                              overflow: 'hidden',
                            }}
                            aria-label={`Preview certificate ${index + 1}`}
                          >
                            <Box
                              component="img"
                              src={fullUrl}
                              alt={fileLabel}
                              sx={{ width: 100, height: 100, objectFit: 'cover', display: 'block' }}
                            />
                          </Box>
                        );
                      }
                      return (
                        <Box key={`${certUrl}-${index}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {getFileIcon(certUrl)}
                          <Link href={fullUrl} target="_blank" rel="noopener noreferrer" underline="hover">
                            {fileLabel}
                          </Link>
                        </Box>
                      );
                    })}
                  </Box>
                );
              })()}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleViewClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={viewImagePreviewOpen}
        onClose={() => {
          setViewImagePreviewOpen(false);
          setViewImagePreviewSrc('');
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Image preview</DialogTitle>
        <DialogContent
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: 'grey.100',
            minHeight: 200,
          }}
        >
          {viewImagePreviewSrc ? (
            <Box
              component="img"
              src={viewImagePreviewSrc}
              alt="Attachment preview"
              sx={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setViewImagePreviewOpen(false);
              setViewImagePreviewSrc('');
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Trainers;
